<script setup lang="ts">
import type { ButtonProps } from '#ui/types';

import { useApiCreateUserFavoriteAsset, useApiDeleteUserFavoriteAsset } from '~/apiClient';
import { type SaleItemEntity, type SaleListingItemEntity } from '~/types/sale.type';

type UiState = 'loading' | 'checked' | 'unchecked';

// PROPS
const { sale } = defineProps({
    sale: { type: Object as PropType<SaleListingItemEntity | SaleItemEntity>, required: true },
});

// EVENTS
const emit = defineEmits<{
    deletedFavorite: [{ saleId: string; assetId: string }];
}>();

// COMPOSABLES
const { isFavoriteAsset, isFavoriteAssetsInitialized, addFavoriteAsset, deleteFavoriteAsset } = useNuxtApp().$favoritesCache;
const { isLoggedIn } = useAuthUtils();
const { mutateAsync: mutateCreate } = useApiCreateUserFavoriteAsset();
const { mutateAsync: mutateDelete } = useApiDeleteUserFavoriteAsset();
const { notifySuccess, notifyGenericError } = useNotification();
const { openAuthModal } = useModals();
const { tt } = useTypedI18n();

// DATA
const BUTTON_STATE_PROPS: Record<UiState, Pick<ButtonProps, 'color' | 'variant' | 'ui' | 'loading'>> = {
    loading: {
        color: 'neutral',
        variant: 'outline',
        ui: { base: 'text-xs outline-1 outline-neutral-200' },
        loading: true,
    },
    checked: {
        color: 'primary',
        variant: 'solid',
        ui: { base: 'text-xs outline-1 outline-neutral-200 hover:bg-primary-400 active:bg-primary-400' },
    },
    unchecked: {
        color: 'neutral',
        variant: 'outline',
        ui: { base: 'text-xs outline-1 outline-neutral-200', leadingIcon: 'text-primary' },
    },
} as const;

const isLoading = ref(false);

// Local delta applied on top of the server-provided favoritesCount after a successful toggle.
// Reset to 0 whenever the server value changes (refetch, navigation, etc.).
const favoritesCountLocalDelta = ref(0);
const displayedFavoritesCount = computed(() => Math.max(0, (sale.favoritesCount ?? 0) + favoritesCountLocalDelta.value));

const uiState = computed<UiState | undefined>(() => {
    if (!isLoggedIn.value) return 'unchecked';

    if (!isFavoriteAssetsInitialized.value) return;

    if (isLoading.value) return 'loading';

    return isFavoriteAsset(sale.mainAssetId) ? 'checked' : 'unchecked';
});

const tooltipText = computed(() => uiState.value && tt(`favorites.tooltip.${uiState.value}`));

const mainAssetTitle = computed(() => `${sale.mainAsset.brand.brandValue} ${sale.mainAsset.model.modelValue}`);

const buttonProps = computed(() => {
    if (!uiState.value) return;

    return {
        ...BUTTON_STATE_PROPS[uiState.value],
        ...(displayedFavoritesCount.value
            ? {
                  label: String(displayedFavoritesCount.value),
              }
            : {
                  square: true,
              }),
    };
});

// METHODS
const onAddFavorite = (): void => {
    isLoading.value = true;

    mutateCreate({ data: { assetIds: [sale.mainAssetId] } })
        .then(() => {
            addFavoriteAsset(sale.mainAssetId);
            favoritesCountLocalDelta.value++;
            notifySuccess({
                title: tt('favorites.notifications.add.title'),
                description: tt('favorites.notifications.add.description', { title: mainAssetTitle.value }),
            });
        })
        .catch(() => {
            notifyGenericError();
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const onDeleteFavorite = (): void => {
    isLoading.value = true;

    mutateDelete({ data: { assetIds: [sale.mainAssetId] } })
        .then(() => {
            deleteFavoriteAsset(sale.mainAssetId);
            favoritesCountLocalDelta.value--;
            emit('deletedFavorite', { saleId: sale.id, assetId: sale.mainAssetId });
            notifySuccess({
                title: tt('favorites.notifications.delete.title'),
                description: tt('favorites.notifications.delete.description', { title: mainAssetTitle.value }),
            });
        })
        .catch(() => {
            notifyGenericError();
        })
        .finally(() => {
            isLoading.value = false;
        });
};

const onToggleFavorite = async (): Promise<void> => {
    if (!isLoggedIn.value) {
        if (!(await openAuthModal({ action: 'ADD_FAVORITE_ASSET' }))) return;

        await until(isFavoriteAssetsInitialized).toBeTruthy();

        if (isFavoriteAsset(sale.mainAssetId)) return;

        onAddFavorite();
    } else {
        if (!isFavoriteAssetsInitialized.value) return;

        isFavoriteAsset(sale.mainAssetId) ? onDeleteFavorite() : onAddFavorite();
    }
};

// WATCHERS
watch(
    () => sale.favoritesCount,
    () => {
        favoritesCountLocalDelta.value = 0;
    }
);
</script>

<template>
    <UTooltip
        :text="tooltipText"
        :content="{
            align: 'start',
            side: 'top',
        }"
        arrow
    >
        <UButton v-if="buttonProps" v-bind="buttonProps" leading-icon="lucide:heart" @click.prevent.stop="onToggleFavorite" />
    </UTooltip>
</template>
